// Riding a legendary challenge: race options, live progress (HUD banner, automatic end) and the end-screen block.
// Progress shown live is an estimate from the same core function the API uses; the saved record is the reference.
import { activeAttempt, challengeRide, challengeValue, findChallenge, CHALLENGE_SESSION_MAX, type ChallengeDef, type Samples } from '@gruvetto/core';
import { state } from '../../data/store.js';
import { esc, fmtDur } from '../dom.js';
import { t, fmtNumber } from '../../i18n/index.js';
import { challengeName } from '../../i18n/content.js';

/** Context of a ride started from the challenges screen: the attempt so far, frozen at the start. */
export interface ChallengeCtx { def: ChallengeDef; base: number; deadline: number | null }

/** A value in the goal's unit, for display (metres, duration, kilometres). */
export function fmtGoal(def: ChallengeDef, v: number): string {
  const V = t.challenges.value;
  return def.goal === 'climb' ? V.climb(fmtNumber(Math.round(v))) : def.goal === 'time' ? V.time(fmtDur(v)) : V.distance(fmtNumber(v / 1000, 1));
}
/** The challenge's target, as a sentence fragment ("8,848 m of climbing", "24 h of riding"). */
export function targetLabel(def: ChallengeDef): string {
  const T = t.challenges.target;
  return def.goal === 'climb' ? T.climb(fmtNumber(def.target)) : def.goal === 'time' ? T.time(fmtNumber(def.target / 3600)) : T.distance(fmtNumber(def.target / 1000));
}

export const challengeRecord = (id: string) => state.progress.challenges?.[id] ?? null;

/** Ride configuration: the challenge's own track, solo, no items, no finish line (the session ends it). */
export function challengeConfig(def: ChallengeDef, now = Date.now()) {
  const a = activeAttempt(def, challengeRecord(def.id), now);
  const ctx: ChallengeCtx = { def, base: challengeValue(def, a), deadline: a ? a.start + def.window * 1000 : null };
  return {
    race: { mode: 'free', track: def.track.id, trackDef: def.track, laps: 1, bots: 0, modeName: challengeName(def.id), rules: { finish: 'open' as const, items: false, bots: false, online: false, draft: false } },
    challenge: ctx,
  };
}

/** Live progress of the attempt with the current ride: value, fraction, and whether the session should end now. */
export function liveProgress(ctx: ChallengeCtx, rec: Samples) {
  const value = ctx.base + challengeValue(ctx.def, challengeRide(ctx.def, rec));
  return { value, frac: Math.min(1, value / ctx.def.target), stop: value >= ctx.def.target || rec.t.length >= CHALLENGE_SESSION_MAX };
}

/** HUD banner, same place and style as an event's. */
export function challengeBannerHtml(ctx: ChallengeCtx): string {
  return `<div class="h-event panel h-challenge"><b>${esc(ctx.def.icon)} ${esc(challengeName(ctx.def.id))}</b><span class="h-ch-txt"></span><div class="bar"><i></i></div></div>`;
}
export function updateBanner(root: HTMLElement, ctx: ChallengeCtx, value: number, frac: number) {
  const txt = root.querySelector('.h-ch-txt'), bar = root.querySelector<HTMLElement>('.h-challenge .bar i');
  if (txt) txt.textContent = t.challenges.hud(fmtGoal(ctx.def, value), fmtGoal(ctx.def, ctx.def.target), Math.floor(frac * 100));
  if (bar) bar.style.width = `${(frac * 100).toFixed(1)}%`;
}

/** End-screen block: completed, or how much is left and how long the clock still runs. */
export function challengeSummaryHtml(ctx: ChallengeCtx, saved: boolean, now = Date.now()): string {
  const T = t.challenges, def = findChallenge(ctx.def.id)!, name = challengeName(def.id);
  if (!saved) return `<div class="card mt-3"><h3>${esc(def.icon)} ${esc(name)}</h3><p class="mut">${esc(T.notCounted)}</p></div>`;
  const rec = challengeRecord(def.id), a = activeAttempt(def, rec, now);
  if (!a && !rec?.completions) return `<div class="card mt-3"><h3>${esc(def.icon)} ${esc(name)}</h3><p class="mut">${esc(T.notCounted)}</p></div>`;
  if (!a) {
    const best = rec?.best != null ? fmtDur(rec.best) : '';
    return `<div class="card ch-done mt-3"><h3>${esc(T.doneTitle(name))}</h3><p class="mut">${esc(T.doneHelp(best))}</p></div>`;
  }
  const value = challengeValue(def, a), frac = Math.min(1, value / def.target);
  const left = fmtGoal(def, Math.max(0, def.target - value)), clock = fmtDur(Math.max(0, (a.start + def.window * 1000 - now) / 1000));
  return `<div class="card mt-3"><h3>${esc(def.icon)} ${esc(T.partTitle(name))}</h3>
    <div class="bar"><i style="width:${(frac * 100).toFixed(1)}%"></i></div>
    <p class="mut small mt-2">${esc(T.progress(fmtGoal(def, value), fmtGoal(def, def.target)))} · ${esc(T.partHelp(left, clock))}</p></div>`;
}
